
import AppliedTax from './AppliedTax'
import Model from './Model'
import Payment from './Payment'
import PaymentMode from './Static/PaymentMode'
import PaymentMethod from './Static/PaymentMethod'

export default class Invoice extends Model {
  constructor (attributes = {}) {
    super(attributes.id)
    this.cancelReason = attributes.cancelReason ?? null // VARCHAR 255
    this.customer_id = attributes.customer_id ?? null // INT
    this.customerJSON = attributes.customerJSON ?? null // TEXT
    this.date = attributes.date ?? this._nowToUtcString() // DATETIME
    this.discountAmount = parseFloat(attributes.discountAmount ?? 0) // DECIMAL 8,2
    this.notAccountable = Boolean(attributes.notAccountable ?? 0) // TINYINT
    this.refunded = Boolean(attributes.refunded ?? 0) // TINYINT
    this.number = attributes.number // VARCHAR 255
    this.prefix = attributes.prefix ?? null // TicketBai STRING (not a DB field)
    this.withoutPrefix = attributes.withoutPrefix ?? null // TicketBai STRING (not a DB field)
    this.counter_id = attributes.counter_id ?? null // INT
    this.order_id = attributes.order_id ?? null // INT
    this.orderContentsIds = attributes.orderContentsIds ?? null // TEXT
    this.orderDiscountAmount = parseFloat(attributes.orderDiscountAmount ?? 0) // DECIMAL 8,2
    this.paymentMode = attributes.paymentMode ?? PaymentMode.STANDARD // TINYINT
    this.subtotal = parseFloat(attributes.subtotal ?? 0) // DECIMAL 8,2
    this.sum = parseFloat(attributes.sum ?? 0) // DECIMAL 8,2
    this.taxAmount = parseFloat(attributes.taxAmount ?? 0) // DECIMAL 8,2
    this.total = parseFloat(attributes.total ?? 0) // DECIMAL 8,2
    this.fiscal_data = attributes.fiscal_data ?? $vm.$store.getters['fiscalCountry/get']('fiscalData') // TEXT

    this.orderPayments = attributes.orderPayments?.map(o => new Payment(o)) ?? []
    this.appliedTaxes = attributes.appliedTaxes?.map(o => new AppliedTax(o)) ?? []

    this._splitedOrderContents = []
  }

  get order () {
    return this._order || (this._order = $vm.$store.getters['orders/get'](this.order_id))
  }

  set order (v) {
    this._order = v
  }

  get dateMoment () {
    return this._parseUtcToLocalMoment(this.date)
  }

  get isPaid () {
    return this.total.greaterThan(0) && this.orderPayments.sum('payAmount').greaterThanOrEqualTo(this.total)
  }

  get isNotPaid () {
    return ! this.isPaid
  }

  get orderContents () {
    return this.orderContentsIds !== null
      ? this.order.orderContents.filter(orderContent => this.orderContentsIds.indexOf(orderContent.id) !== -1)
      : this._splitedOrderContents
  }

  get canBeRefunded () {
    return ! this.refunded
      && this.orderPayments.every(payment => PaymentMethod.isRefundable(payment.paymentMethod))
  }

  get accountable () {
    return ! this.notAccountable
  }

  addPayment (attributes) {
    this.$log('Invoice', `Payment (${attributes.paymentMethod}): ` + parseFloat(attributes.payAmount).toFixed(2))
    if (parseFloat(attributes.change).notEqualTo(0)) {
      this.$log('Invoice', `Change: ` + parseFloat(attributes.change).toFixed(2))
    }
    if (parseFloat(attributes.tipAmount).notEqualTo(0)) {
      this.$log('Invoice', `Tip: ` + parseFloat(attributes.tipAmount).toFixed(2))
    }
    this.orderPayments.push(new Payment(attributes))
  }

  // heavily inspired by Xef’s APP GSOrderInvoice.m @ calculateSplittedToPay but also deeply refactored after few months
  setSummary () {
    const contents = this._getContentsForSummary()

    if (this.paymentMode == PaymentMode.STANDARD && this.order?.discount) {
      this.orderDiscountAmount = this.order.orderDiscountAmount || 0
      this._recalculateContentsWithOrderDiscount(contents)
    }

    this.discountAmount = Math.roundToCurrency(this.orderDiscountAmount + contents.sum('discount'))
    this.sum = Math.roundToCurrency(contents.sum('sum'))
    this.subtotal = Math.roundToCurrency(contents.sum('subtotal'))
    this.total = Math.roundToCurrency(contents.sum('total'))

    this._applyTaxes(contents)
    this.taxAmount = this.appliedTaxes.length > 0
      ? this.appliedTaxes.sum('tax_amount').roundTo(2)
      : (this.total - this.subtotal).roundTo(2)
  }

  _getContentsForSummary () {
    return this.orderContents.map(content => ({
      discount: content.discountAmount,
      sum: content.priceWithExtrasIndividual * content.quantity, // _getPriceWithExtras function is not available when the content is not an OrderContent
      subtotal: content.subtotal,
      total: content.total,
      appliedTaxes: content.appliedTaxes,
    }))
  }

  _recalculateContentsWithOrderDiscount (contents) {
    let discountFactor = 1
    if (Number(this.order.total).greaterThan(0)) {
      const total = !! $vm.$store.getters['rummage/business']('taxIncluded')
        ? contents.sum('total')
        : contents.sum('subtotal')
      discountFactor = this.orderDiscountAmount / total
    }
    contents.forEach(content => {
      content.total = Math.roundToCurrency(content.total - content.total * discountFactor)
      content.subtotal = Number(content.total / (content.taxPercentage / 100 + 1)).roundTo(4)
      content.appliedTaxes.forEach(appliedTax => {
        const total = appliedTax.base_amount + appliedTax.tax_amount
        const totalWithOrderDiscount = (total - total * discountFactor).roundTo(4)
        appliedTax.base_amount = (appliedTax.base_amount - appliedTax.base_amount * discountFactor).roundTo(4)
        appliedTax.tax_amount = totalWithOrderDiscount - appliedTax.base_amount
      })
      content.taxAmount = content.appliedTaxes.sum('tax_amount')
    })
  }

  _applyTaxes (contents) {
    this.appliedTaxes = Object.values(contents.flatMap(content => content.appliedTaxes).groupBy('tax_id'))
      .map(appliedTaxes => new AppliedTax({
        tax_id: appliedTaxes[0].tax_id,
        tax_name: appliedTaxes[0].tax_name,
        tax_percentage: appliedTaxes[0].tax_percentage,
        base_amount: appliedTaxes.sum('base_amount'),
        tax_amount: appliedTaxes.sum('tax_amount'),
      }))
  }

  toJSON () {
    return {
      id: this.id,
      customer_id: this.customer_id,
      customerJSON: this.customerJSON,
      date: this.date,
      discountAmount: this.discountAmount,
      notAccountable: this.notAccountable,
      number: this.number,
      counter_id: this.counter_id,
      order_id: this.order_id,
      orderContentsIds: this.orderContentsIds,
      orderDiscountAmount: this.orderDiscountAmount,
      paymentMode: this.paymentMode,
      subtotal: this.subtotal,
      sum: this.sum,
      taxAmount: this.taxAmount,
      appliedTaxes: this.appliedTaxes,
      total: this.total,
      orderPayments: this.orderPayments,
    }
  }
}
